/* ============================================================
   Богатая боковая навигация (как в макете): сгруппированные
   разделы с под-пунктами и живыми счётчиками.
   Группы: Объекты / Документы / Проектирование.
   Активный пункт определяется по текущему маршруту + query-параметрам.
   ============================================================ */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { navigationApi } from '@/api';
import type { NavCounts } from '@/types';
import './rich-sidebar.css';

interface SubItem {
  label: string;
  to: string;                       // путь назначения
  query?: Record<string, string>;   // query-параметры фильтра
  countKey?: keyof NavCounts;       // ключ счётчика для бейджа
  accent?: 'blue' | 'green' | 'yellow' | 'red';
}
interface Group {
  title: string;
  items: SubItem[];
}

const GROUPS: Group[] = [
  {
    title: 'Объекты',
    items: [
      { label: 'Действующие', to: '/objects', query: { status: 'active' }, countKey: 'objects_active', accent: 'blue' },
      { label: 'Законченные', to: '/objects', query: { status: 'completed' }, countKey: 'objects_completed', accent: 'green' },
      { label: 'Гарантийные обяз.', to: '/contracts', query: { status: 'warranty' }, countKey: 'contracts_warranty', accent: 'yellow' },
      { label: 'Договоры на подписи', to: '/contracts', query: { status: 'signing' }, countKey: 'contracts_signing', accent: 'red' },
    ],
  },
  {
    title: 'Документы',
    items: [
      { label: 'Договоры (заключение)', to: '/contracts', query: { type: 'customer' }, countKey: 'contracts_customer' },
      { label: 'Договоры с подряд.', to: '/contracts', query: { type: 'subcontract' }, countKey: 'contracts_subcontract' },
      { label: 'Договоры с поставщ.', to: '/contracts', query: { type: 'supply' }, countKey: 'contracts_supply' },
    ],
  },
  {
    title: 'Проектирование',
    items: [
      { label: 'Проверка проектов', to: '/projects', query: { status: 'review' }, countKey: 'projects_review', accent: 'yellow' },
      { label: 'Проверка смет', to: '/estimates', query: { status: 'review' }, countKey: 'estimates_review', accent: 'yellow' },
      { label: 'Создание проектов', to: '/projects', query: { new: '1' } },
      { label: 'Создание смет', to: '/estimates', query: { new: '1' } },
    ],
  },
];

export function RichSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { data: counts } = useQuery({
    queryKey: ['nav-counts'],
    queryFn: () => navigationApi.counts(),
    staleTime: 15_000,
  });

  const isActive = (item: SubItem) => {
    if (location.pathname !== item.to) return false;
    if (!item.query) return Array.from(searchParams.keys()).length === 0;
    return Object.entries(item.query).every(([k, v]) => searchParams.get(k) === v);
  };

  const go = (item: SubItem) => {
    const qs = item.query ? '?' + new URLSearchParams(item.query).toString() : '';
    navigate(item.to + qs);
  };

  return (
    <nav className="rich-sb">
      {GROUPS.map((group) => (
        <div key={group.title} className="rich-sb__group">
          <div className="rich-sb__group-title">{group.title}</div>
          {group.items.map((item) => {
            const count = item.countKey && counts ? counts[item.countKey] : undefined;
            return (
              <button
                key={item.label + item.to + JSON.stringify(item.query)}
                className={`rich-sb__item${isActive(item) ? ' rich-sb__item--active' : ''}`}
                onClick={() => go(item)}
              >
                <span className="rich-sb__label">{item.label}</span>
                {count !== undefined && count > 0 && (
                  <span className={`rich-sb__count rich-sb__count--${item.accent ?? 'default'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
